import { InputText } from 'ui/inputs';
import styled from 'styled-components';
import { colors, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { getIsInvalidHexColor, getIsEditMode } from 'modules/templateEditor/common';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { formatHexForRoku, formatHexForState, formatRokuHexForUI } from '../../../utils';
import { SETTINGS_DISPLAY_CONTENT } from '../../../constants';

const Container = styled.div`
  display: flex;
`;

export const PickerWrappper = styled.div`
  width: 100%;
  padding-left: ${spacing.quarter};
`;

export const ColorPreviewWrapper = styled.div`
  border: ${spacing.single} solid ${colors.grey15};
  border-radius: ${spacing.quarter};
  padding: ${spacing.quarter};
`;

export const ColorPreview = styled.div<ColorPreviewProps>`
  width: ${(props) => (props.isEditMode ? spacing.double : pxToRem(20))};
  height: ${(props) => (props.isEditMode ? spacing.double : pxToRem(20))};
  background: ${(props) => props.hexColor};
  border-radius: ${spacing.quarter};
  margin: auto;
`;

export const StyledInputText = styled(InputText)`
  width: 100%;

  & > input {
    width: 100%;
  }
`;

type ColorPreviewProps = {
  hexColor: string | undefined;
  isEditMode: boolean;
};

type ColorPickerProps = {
  hexColor?: string;
  onChange: (hex: string) => void;
  dataTestIdentifier: number | string;
};

export const ColorPicker = ({ hexColor, onChange, dataTestIdentifier }: ColorPickerProps) => {
  const prettifiedHexValue = formatRokuHexForUI(hexColor); // converts 0xFFFFFF => FFFFFF
  const cssHexValue = formatHexForState(prettifiedHexValue); // #FFFFFF

  const { isEditMode: isEditModeContext } = useEditableFieldsContext();
  const { view } = useQueryParamContext();

  const isEditMode = getIsEditMode(view, isEditModeContext);

  const hexColorError = getIsInvalidHexColor(cssHexValue);

  return (
    <Container>
      <ColorPreviewWrapper>
        <ColorPreview hexColor={cssHexValue} isEditMode={isEditMode} />
      </ColorPreviewWrapper>
      <PickerWrappper>
        <StyledInputText
          Prefix={SETTINGS_DISPLAY_CONTENT.backgroundOverlay.hexColorPrefix}
          data-test={`roku-color-picker-${dataTestIdentifier}`}
          hasError={hexColorError}
          id="hexColor"
          onChange={(hexValue) => onChange(formatHexForRoku(hexValue))}
          placeholder={SETTINGS_DISPLAY_CONTENT.backgroundOverlay.hexColorPlaceholder}
          type="text"
          value={prettifiedHexValue}
          width="inherit"
          maxLength={6}
        />
      </PickerWrappper>
    </Container>
  );
};
